import { useToast, useTranslation, useAuthentication } from "@repo/core";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiPatchMutation, useApiQuery } from "./useApiQuery.ts";

export type EmailSubscriptionPayload = {
    subscribe: boolean;
};

export type PreferredLanguage = "en" | "cs";

export type UserPreferences = {
    preferredLanguage: PreferredLanguage | null;
};

// Keyed by user so another account signing in on the same browser never sees cached preferences
const userPreferencesKey = (userId?: string) => ["user", "preferences", userId];

/**
 * Preferences stored in UsersTable, used to restore them on another device.
 * Disabled until a user is signed in.
 */
export const useUserPreferences = (userId?: string) =>
    useApiQuery<UserPreferences>({
        path: "/user/preferences",
        queryKey: userPreferencesKey(userId),
        enabled: !!userId,
        staleTime: Infinity,
        retry: false,
    });

/**
 * Saves preferences silently: a failed background sync must not interrupt the user.
 */
export const useUpdateUserPreferences = (userId?: string) => {
    const queryClient = useQueryClient();

    const mutation = useApiPatchMutation<UserPreferences, Partial<UserPreferences>>({
        getPath: () => "/user/preferences",
        getConfig: (data) => ({
            body: data,
        }),
    });

    // Per-call callbacks: useApiPatchMutation replaces a hook-level onSuccess with its own
    const { mutate } = mutation;
    const updatePreferences = useCallback(
        (preferences: Partial<UserPreferences>) =>
            mutate(preferences, {
                onSuccess: (data) => queryClient.setQueryData(userPreferencesKey(userId), data),
                onError: (error) => console.error("User preferences update error:", error),
            }),
        [mutate, queryClient, userId]
    );

    return { updatePreferences };
};

export const useUserApi = () => {
    const { addErrorMessage, addSuccessMessage } = useToast();
    const { t } = useTranslation();
    const { refreshAuth } = useAuthentication();

    const mutation = useApiPatchMutation<{ success: boolean }, EmailSubscriptionPayload>({
        getPath: () => "/user/email-subscription",
        getConfig: (data) => ({
            body: data,
        }),
    });

    const changeEmailSubscription = async (subscribe: boolean) => {

        try {
            const result = await mutation.mutateAsync({
                subscribe,
            });

            console.log('Email subscription updated:', result);

            // Show success message
            if (subscribe) {
                addSuccessMessage(t('emailSubscription.subscribeSuccess', 'Successfully subscribed to mailing list'));
            } else {
                addSuccessMessage(t('emailSubscription.unsubscribeSuccess', 'Successfully unsubscribed from mailing list'));
            }

            // Refresh user data to update the UI
            await refreshAuth();

        } catch (error: any) {
            console.error('Email subscription error:', error);
            addErrorMessage(t('emailSubscription.error', 'Failed to update email subscription'));
        }
    };

    return {
        changeEmailSubscription,
    };
}
